/** Temas predeterminados del menú público. Acentos fijos: estándar #18181B, premium #F5B041 (sin `primary_color`). */

export const MENU_THEME_STANDARD = 'standard';
export const MENU_THEME_PREMIUM_LIGHT = 'premium_light';
export const MENU_THEME_PREMIUM_DARK = 'premium_dark';

/** Acento menú estándar (CTA, precios en tema clásico). */
export const MENU_STANDARD_ACCENT = '#18181B';

/** Acento dorado fijo en premium claro y oscuro. */
export const MENU_PREMIUM_ACCENT = '#F5B041';

/** Colores fijos por tema (no persistidos). */
const PRESET_COLORS = {
  [MENU_THEME_PREMIUM_LIGHT]: {
    background_color: '#FAFAF8',
    text_color: '#111111',
    muted_text_color: '#666666',
    card_background_color: '#FFFFFF',
    card_border_color: '#EAEAEA',
    button_text_color: '#111111',
  },
  [MENU_THEME_PREMIUM_DARK]: {
    background_color: '#070707',
    text_color: '#FFFFFF',
    muted_text_color: '#B8B8B8',
    card_background_color: '#111111',
    card_border_color: 'rgba(255,255,255,0.12)',
    button_text_color: '#111111',
  },
};

/** Degradados fijos del banner / hero (no editables). */
const BANNER_GRADIENT = {
  [MENU_THEME_PREMIUM_DARK]:
    'linear-gradient(to bottom, rgba(0,0,0,0.45), rgba(0,0,0,0.92))',
  [MENU_THEME_PREMIUM_LIGHT]:
    'linear-gradient(to bottom, rgba(255,255,255,0.10), rgba(250,250,248,0.96))',
};

/**
 * @param {object | null | undefined} business
 * @returns {'standard' | 'premium_light' | 'premium_dark'}
 */
export function getMenuTheme(business) {
  const raw = String(business?.menu_theme ?? MENU_THEME_STANDARD)
    .trim()
    .toLowerCase();
  if (raw === MENU_THEME_PREMIUM_DARK) return MENU_THEME_PREMIUM_DARK;
  if (raw === MENU_THEME_PREMIUM_LIGHT) return MENU_THEME_PREMIUM_LIGHT;
  if (raw === 'light' || raw === MENU_THEME_STANDARD) return MENU_THEME_STANDARD;
  return MENU_THEME_STANDARD;
}

export function isPremiumDarkMenu(business) {
  return getMenuTheme(business) === MENU_THEME_PREMIUM_DARK;
}

export function isPremiumLightMenu(business) {
  return getMenuTheme(business) === MENU_THEME_PREMIUM_LIGHT;
}

export function isPremiumMenu(business) {
  const t = getMenuTheme(business);
  return t === MENU_THEME_PREMIUM_LIGHT || t === MENU_THEME_PREMIUM_DARK;
}

/**
 * Paleta efectiva del tema premium (solo valores fijos del preset).
 * @returns {Record<string, string>}
 */
export function getMenuBranding(business) {
  const theme = getMenuTheme(business);
  if (theme === MENU_THEME_STANDARD) return {};
  const preset = PRESET_COLORS[theme];
  return preset ? { ...preset } : {};
}

/** Acento visual del menú público: fijo por tema (no usa `business.primary_color`). */
export function getMenuAccentColor(business) {
  return getMenuTheme(business) === MENU_THEME_STANDARD
    ? MENU_STANDARD_ACCENT
    : MENU_PREMIUM_ACCENT;
}

/** @deprecated alias */
export const getMenuAccentHex = getMenuAccentColor;

export function getMenuPageStyle(business) {
  if (getMenuTheme(business) === MENU_THEME_STANDARD) return {};
  const b = getMenuBranding(business);
  return {
    backgroundColor: b.background_color,
    color: b.text_color,
    minHeight: '100vh',
  };
}

export function getMenuCardStyle(business) {
  if (getMenuTheme(business) === MENU_THEME_STANDARD) return {};
  const b = getMenuBranding(business);
  return {
    backgroundColor: b.card_background_color,
    borderColor: b.card_border_color,
    borderWidth: 1,
    borderStyle: 'solid',
  };
}

export function getMenuPrimaryButtonStyle(business) {
  if (getMenuTheme(business) === MENU_THEME_STANDARD) return {};
  const b = getMenuBranding(business);
  const accent = getMenuAccentColor(business);
  return {
    backgroundColor: accent,
    color: b.button_text_color,
    borderColor: accent,
    boxShadow: `0 0 28px -6px ${accent}77`,
  };
}

export function getMenuMutedTextStyle(business) {
  if (getMenuTheme(business) === MENU_THEME_STANDARD) return {};
  return { color: getMenuBranding(business).muted_text_color };
}

/** Estilos de tarjetas destacadas (oferta, promo, más vendido) según tema claro/oscuro. */
export function getCatalogHighlightAccentStyles(business, kind) {
  const theme = getMenuTheme(business);
  const isDark = theme === MENU_THEME_PREMIUM_DARK;

  const palettes = {
    daily: {
      standard: {
        badgeClass: 'bg-amber-500/15 text-amber-950 dark:text-amber-100 border border-amber-500/25',
        iconClass: 'text-amber-600 dark:text-amber-400',
      },
      premium_light: {
        badgeClass: 'bg-amber-500/15 text-amber-950 border border-amber-500/25',
        iconClass: 'text-amber-600',
      },
      premium_dark: {
        badgeClass: 'bg-amber-500/20 text-amber-100 border border-amber-400/35',
        iconClass: 'text-amber-400',
      },
    },
    bestseller: {
      standard: {
        badgeClass: 'bg-orange-500/15 text-orange-950 dark:text-orange-100 border border-orange-500/25',
        iconClass: 'text-orange-600 dark:text-orange-400',
      },
      premium_light: {
        badgeClass: 'bg-orange-500/15 text-orange-950 border border-orange-500/25',
        iconClass: 'text-orange-600',
      },
      premium_dark: {
        badgeClass: 'bg-orange-500/20 text-orange-100 border border-orange-400/35',
        iconClass: 'text-orange-400',
      },
    },
    promo: {
      standard: {
        badgeClass: 'bg-violet-500/15 text-violet-950 dark:text-violet-100 border border-violet-500/25',
        iconClass: 'text-violet-600 dark:text-violet-400',
      },
      premium_light: {
        badgeClass: 'bg-violet-500/15 text-violet-950 border border-violet-500/25',
        iconClass: 'text-violet-600',
      },
      premium_dark: {
        badgeClass: 'bg-violet-500/20 text-violet-100 border border-violet-400/35',
        iconClass: 'text-violet-400',
      },
    },
  };

  const palette = palettes[kind] || palettes.promo;
  if (theme === MENU_THEME_PREMIUM_LIGHT) return palette.premium_light;
  if (isDark) return palette.premium_dark;
  return palette.standard;
}

/** Título de sección del catálogo acorde al tema (fondo claro → texto oscuro y viceversa). */
export function getCatalogSectionTitleClass(business, standard) {
  if (!standard) return '';
  return 'text-foreground';
}

/** Overlay del banner en welcome (y hero de catálogo). */
export function getMenuBannerOverlayStyle(business) {
  const t = getMenuTheme(business);
  if (t === MENU_THEME_STANDARD) return {};
  const g = BANNER_GRADIENT[t];
  return g ? { background: g } : {};
}

export const getMenuCatalogHeroOverlayStyle = getMenuBannerOverlayStyle;

/** Layout catálogo en tema standard (Tailwind / shadcn). */
export const STANDARD_CATALOG_LAYOUT = {
  pageBg: 'bg-background',
  header: 'bg-background border-b',
  headerTitle: '',
  headerCart: '',
  searchInput: '',
  searchIcon: '',
  productCard: '',
  productTitle: '',
  productMuted: 'text-muted-foreground',
  emptyHint: 'text-muted-foreground',
  emptyResetButton: '',
  modalSurface: '',
  qtyBar: 'bg-secondary',
  ghostNav: '',
  categoryOutline: '',
  errorCard: '',
  fabExtraClass: '',
};

/** Botón +/- en modal (premium claro). */
export function getPremiumModalIconButtonClass(business) {
  if (getMenuTheme(business) !== MENU_THEME_PREMIUM_LIGHT) return '';
  return 'border-amber-400/80 text-amber-800 bg-white hover:bg-amber-50 hover:text-amber-900';
}
